import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { measureAnnotationInspectorContent } from "./measureAnnotationInspectorContent";
import { ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM } from "./MediaAnnotationStudioConstraint";

/** 현재 preview 배치와 무관하게 원래 패널 예산으로 공간을 판정한다. */
export function useCapturedFramePreviewLayout(
  viewportRef: RefObject<HTMLElement | null>,
  frameCount: number,
  layoutKey: string,
  open: boolean,
  compactToolsHeight = 0,
  onCompactTools?: (compact: boolean) => void,
  allowAutomaticCompaction = true,
) {
  const [canDock, setCanDock] = useState(false);
  const [fitsCurrent, setFitsCurrent] = useState(false);
  const [detached, setDetached] = useState(false);
  const focusOnDock = useRef(false);
  const wasInline = useRef(false);
  const inline = fitsCurrent && !detached;
  useLayoutEffect(() => {
    // 도킹 때문에 잠시 축약한 도구만 복원한다. 최초 도킹의 높이 측정 전에는 해제하지 않는다.
    if (!open || (wasInline.current && !inline)) onCompactTools?.(false);
    wasInline.current = inline;
  }, [inline, open, onCompactTools]);
  useLayoutEffect(() => {
    if (open && !detached && canDock && !fitsCurrent && allowAutomaticCompaction) {
      onCompactTools?.(true);
    }
  }, [open, detached, canDock, fitsCurrent, allowAutomaticCompaction, onCompactTools]);
  useLayoutEffect(() => {
    const inspector = viewportRef.current?.closest<HTMLElement>("[data-annotation-inspector]");
    const frames = inspector?.parentElement?.querySelector<HTMLElement>(
      "[data-captured-frame-section]",
    );
    if (!inspector || !frames) return;
    const update = () => {
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const toolsHeight = measureAnnotationInspectorContent(inspector);
      // 프레임은 한 행과 pagination까지 양보해 미리보기의 읽기 크기를 먼저 확보한다.
      const frameHeight = Number.parseFloat(getComputedStyle(frames).minHeight) || 0;
      const gap = Number.parseFloat(getComputedStyle(inspector).rowGap) || 0;
      const available =
        inspector.getBoundingClientRect().height +
        frames.getBoundingClientRect().height -
        toolsHeight -
        frameHeight -
        gap;
      const fits = available >= ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM * rem + 1;
      if (fits && !inline && document.activeElement?.closest("[data-annotation-reference-preview]"))
        focusOnDock.current = true;
      setFitsCurrent(fits);
      const minimumToolsHeight = Math.max(
        Number.parseFloat(getComputedStyle(inspector).minHeight) || 0,
        compactToolsHeight > 0 ? Math.min(toolsHeight, compactToolsHeight) : toolsHeight,
      );
      setCanDock(
        available + toolsHeight - minimumToolsHeight >=
          ANNOTATION_CAPTURED_FRAME_PREVIEW_MIN_HEIGHT_REM * rem + 1,
      );
    };
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(inspector);
    observer?.observe(frames);
    const tools = inspector.querySelector(
      "[data-annotation-tools-content], [data-annotation-placeholder-content]",
    );
    if (tools) observer?.observe(tools);
    return () => observer?.disconnect();
  }, [viewportRef, frameCount, layoutKey, inline, open, compactToolsHeight]);

  useLayoutEffect(() => {
    if (!inline || !open || !focusOnDock.current) return;
    focusOnDock.current = false;
    queueMicrotask(() =>
      viewportRef.current
        ?.closest("[data-annotation-inspector]")
        ?.querySelector<HTMLElement>("[data-preview-placement-toggle]")
        ?.focus(),
    );
  }, [inline, open, viewportRef]);

  const detach = useCallback(() => setDetached(true), []);
  const dock = useCallback(() => {
    if (!canDock) return;
    focusOnDock.current = true;
    if (!fitsCurrent) onCompactTools?.(true);
    setDetached(false);
  }, [canDock, fitsCurrent, onCompactTools]);
  return { inline, canDock, dockRequiresCompact: canDock && !fitsCurrent, detach, dock };
}
